/**
 * BackupMeter — pure slow-charge resource (no Phaser, Node-testable).
 *
 * Fills passively over time (BACKUP_CHARGE_PER_TICK per game frame) and gets
 * a small bonus whenever the player deals or takes a hit. When full
 * (charge >= 1.0) isBackupReady() returns true; consumeBackupMeter() resets to 0.
 */

/** Passive charge per game tick — fills in ~20 s at 60 fps. */
export const BACKUP_CHARGE_PER_TICK = 1 / (20 * 60);

/** Bonus charge per hit dealt or taken. */
export const BACKUP_HIT_CHARGE = 0.06;

/** Threshold at which the meter is considered ready. */
export const BACKUP_READY_THRESHOLD = 1.0;

export interface BackupMeterState {
  charge: number; // 0..BACKUP_READY_THRESHOLD
}

export function createBackupMeter(): BackupMeterState {
  return { charge: 0 };
}

/** Advance passive charging by one game tick. */
export function tickBackupMeter(state: BackupMeterState): void {
  state.charge = Math.min(BACKUP_READY_THRESHOLD, state.charge + BACKUP_CHARGE_PER_TICK);
}

/** Add a hit bonus (call on hit dealt or received). */
export function chargeBackupMeter(
  state: BackupMeterState,
  amount: number = BACKUP_HIT_CHARGE,
): void {
  state.charge = Math.min(BACKUP_READY_THRESHOLD, state.charge + amount);
}

/** True when the meter is full and the backup call can be triggered. */
export function isBackupReady(state: BackupMeterState): boolean {
  return state.charge >= BACKUP_READY_THRESHOLD;
}

/**
 * Consume the meter, resetting it to 0.
 * Returns false if not ready (no-op); true on success.
 */
export function consumeBackupMeter(state: BackupMeterState): boolean {
  if (!isBackupReady(state)) return false;
  state.charge = 0;
  return true;
}

/** 0..1 fraction suitable for HUD bar fill. */
export function backupFraction(state: BackupMeterState): number {
  return Math.max(0, Math.min(1, state.charge / BACKUP_READY_THRESHOLD));
}
